<script setup lang="ts">
import type { RCAppConfig } from '@/config/rcapp.config'
import type { ICreateRSAForm, ICreateRSAVersionForm, IRSAForm } from '@/types/rc/forms'
import { createRSA, createRSAVersion } from '@/services/rc.service'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types'
import { inject, reactive, ref } from 'vue'
import RlUploadPdf from '@/components/common/upload/RlUploadPdf.vue'

const open = defineModel<boolean>()
const props = defineProps<{
  customerId: number
  rsaDetails?: { number: string; expired: boolean }
}>()
const emit = defineEmits(['dialogClosed'])

const RCAPP_CONFIG = inject<RCAppConfig>('rcapp.config')
const monthsTermOptions = ref<OptionType[]>(
  RCAPP_CONFIG?.customer.rsa.termOptions ?? [{ label: '12 Months', value: 12 }]
)

const uploadRef = ref()

const formRef = ref<FormInstance>()
const form = reactive<IRSAForm>({
  start_date: new Date(),
  months_term: monthsTermOptions.value[0].value,
  signed_document: undefined,
})

const rules = reactive<FormRules<ICreateRSAForm>>({
  start_date: [{ required: true, message: 'Start date is required.' }],
  months_term: [{ required: true, message: 'Term is required.' }],
  signed_document: [{ required: true, message: 'Signed document is required.' }],
})

const queryClient = useQueryClient()
const {
  mutate: create,
  isPending: createIsPending,
  isError: createIsError,
  error: createError,
  reset: resetCreate,
} = useMutation({
  mutationFn: (rsaForm: ICreateRSAForm) => createRSA(rsaForm),
  onSuccess: () => {
    resetFormCloseDialog()
    ElMessage({
      type: 'success',
      message: 'Successfully created RSA!',
    })
    queryClient.invalidateQueries({ queryKey: ['customer-rsa'] })
  },
})

const {
  mutate: createVersion,
  isPending: createVersionIsPending,
  isError: createVersionIsError,
  error: createVersionError,
  reset: resetCreateVersion,
} = useMutation({
  mutationFn: (rsaVersionForm: ICreateRSAVersionForm) => createRSAVersion(rsaVersionForm),
  onSuccess: () => {
    resetFormCloseDialog()
    ElMessage({
      type: 'success',
      message: 'Successfully created new RSA version!',
    })
    queryClient.invalidateQueries({ queryKey: ['customer-rsa'] })
  },
})

/**
 * Resets form and closes dialog.
 */
const resetFormCloseDialog = () => {
  resetCreateVersion()
  resetCreate()
  resetForm(formRef.value)
  open.value = false
}

/**
 * Handles form submit (RSA "create" or "create version", depending on props).
 *
 * @param formEl form instance.
 */
const submitForm = async (formEl: FormInstance | undefined) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  await formEl?.validate(async (valid, fields) => {
    if (valid) {
      if (props.rsaDetails) {
        const createVersionForm: ICreateRSAVersionForm = {
          ...form,
          rsa_number: props.rsaDetails.number,
        }
        createVersion(createVersionForm)
      } else {
        const createForm: ICreateRSAForm = {
          ...form,
          customer_id: props.customerId,
        }
        create(createForm)
      }
    }
  })
}

/**
 * Resets form, clears upload files.
 *
 * @param formEl form instance.
 */
const resetForm = (formEl: FormInstance | undefined) => {
  formEl?.resetFields()
  uploadRef.value?.clear()
}
</script>

<template>
  <el-dialog
    v-model="open"
    :title="rsaDetails ? `${rsaDetails.number} - Create New Version` : 'Create New RSA'"
    :show-close="false"
    :width="600"
    @closed="emit('dialogClosed')"
  >
    <el-form ref="formRef" :model="form" :rules="rules" :label-width="192" label-position="left" hide-required-asterisk>
      <el-form-item label="Start Date" prop="start_date">
        <el-date-picker v-model="form.start_date" placeholder="Select start date..." />
      </el-form-item>
      <el-form-item label="Term" prop="months_term">
        <el-select-v2 v-model="form.months_term" :options="monthsTermOptions" />
      </el-form-item>
      <el-form-item label="Signed Document" prop="signed_document">
        <rl-upload-pdf ref="uploadRef" v-model="form.signed_document" />
      </el-form-item>
    </el-form>
    <rl-error-alert v-if="createIsError" :error="createError" />
    <rl-error-alert v-if="createVersionIsError" :error="createVersionError" />
    <template #footer>
      <el-button :disabled="createIsPending || createVersionIsPending" @click="resetFormCloseDialog">Cancel</el-button>
      <el-button type="primary" :loading="createIsPending || createVersionIsPending" @click="submitForm(formRef)"
        >Create</el-button
      >
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
/* Override EP components */
:deep() {
  .el-date-editor,
  .el-select-v2 {
    flex: 1;
  }

  .el-upload-dragger,
  .el-upload--text {
    color: var(--text-tertiary);

    &.hint {
      font-size: 12px;
    }

    .highlight {
      color: var(--text-brand);
    }
  }
}
</style>
